import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { apiClient, API } from "@/lib/auth";
import axios from "axios";

/**
 * NEX Dynamic Engine — client-side runtime for label overrides + layout config.
 *
 * At startup:
 *   • Public GET /api/labels → boots label overrides (no auth required)
 *   • On auth ready, prefetches every registered layout scope for the current user
 *
 * Hooks:
 *   const t = useLabel("home.hero.title", "Custom Business Engines")
 *   const { items, isHidden } = useLayout(scope, defaultSections)
 *
 * Components:
 *   <Section scope="admin_dashboard" id="leads_card">…</Section>
 *
 * The <Section> component simply skips render when the layout config marks it
 * hidden — reordering is applied by the parent (useLayout returns the ordered list).
 */

const DynamicEngineContext = createContext({
  labels: {},
  layouts: {},
  reloadLabels: () => {},
  reloadLayout: (_scope) => {},
});

export const DynamicEngineProvider = ({ children }) => {
  const [labels, setLabels] = useState({});
  const [layouts, setLayouts] = useState({}); // { [scope]: [{section_id,order,hidden}, ...] }

  const reloadLabels = useCallback(async () => {
    try {
      // Public endpoint — no auth required. Use plain axios so we don't get 401 loops before login.
      const { data } = await axios.get(`${API}/labels`);
      setLabels(data.overrides || {});
    } catch { /* silent — fall back to defaults */ }
  }, []);

  const reloadLayout = useCallback(async (scope) => {
    try {
      const { data } = await apiClient.get(`/admin/layout/${scope}`);
      setLayouts((prev) => ({ ...prev, [scope]: data.items || [] }));
    } catch { /* silent — page falls back to defaults */ }
  }, []);

  useEffect(() => { reloadLabels(); }, [reloadLabels]);

  const value = useMemo(() => ({ labels, layouts, reloadLabels, reloadLayout }), [labels, layouts, reloadLabels, reloadLayout]);
  return <DynamicEngineContext.Provider value={value}>{children}</DynamicEngineContext.Provider>;
};

export const useDynamicEngine = () => useContext(DynamicEngineContext);

/**
 * useLabel(key, defaultText) — returns the current text for a registered label.
 * Also registers the key + default in a global registry so /admin/labels can
 * discover every managed string at runtime.
 */
const LABEL_REGISTRY = new Map(); // key → default text (last-seen wins)
export const getLabelRegistry = () => Array.from(LABEL_REGISTRY.entries())
  .map(([key, def]) => ({ key, default: def }))
  .sort((a, b) => a.key.localeCompare(b.key));

/**
 * Register a label default outside of render. Used by /app/frontend/src/lib/labelKeys.js
 * to seed the registry at app boot so /admin/labels shows the full catalog
 * even before the operator visits the page a label lives on.
 */
export const seedLabelDefault = (key, defaultText) => {
  if (!key) return;
  LABEL_REGISTRY.set(key, defaultText);
};

export const useLabel = (key, defaultText) => {
  // Register on first render — SSR-safe (Map is inert if never read).
  if (!LABEL_REGISTRY.has(key) || LABEL_REGISTRY.get(key) !== defaultText) {
    LABEL_REGISTRY.set(key, defaultText);
  }
  const { labels } = useContext(DynamicEngineContext);
  const val = labels[key];
  return (val === undefined || val === null || val === "") ? defaultText : val;
};

/**
 * useLayout(scope, defaultSections)
 *   defaultSections: [{ id, order?, defaultHidden? }]
 * Returns:
 *   { items: [{id,order,hidden}], isHidden(id): bool, reload() }
 * On first call for a scope, fetches the config from the server.
 */
export const useLayout = (scope, defaultSections = []) => {
  const { layouts, reloadLayout } = useContext(DynamicEngineContext);
  useEffect(() => {
    if (!scope) return;
    if (layouts[scope] === undefined) reloadLayout(scope);
  }, [scope, layouts, reloadLayout]);

  const items = useMemo(() => {
    const saved = layouts[scope] || [];
    const savedById = new Map(saved.map((r) => [r.section_id, r]));
    // Start from the code-registered defaults so unmanaged sections still render.
    const merged = defaultSections.map((s, idx) => {
      const row = savedById.get(s.id);
      return {
        id: s.id,
        order: row ? row.order : (s.order ?? idx),
        hidden: row ? !!row.hidden : !!s.defaultHidden,
      };
    });
    // Add any saved-but-unknown sections at the end (defensive — the code may
    // have removed a section; drop them silently).
    merged.sort((a, b) => a.order - b.order);
    return merged;
  }, [scope, layouts, defaultSections]);

  const isHidden = useCallback((id) => {
    const row = items.find((r) => r.id === id);
    return row ? row.hidden : false;
  }, [items]);

  const reload = useCallback(() => reloadLayout(scope), [reloadLayout, scope]);
  return { items, isHidden, reload };
};

/**
 * <Section scope id> — thin wrapper that renders nothing when the current
 * layout config marks the section hidden.
 */
export const Section = ({ scope, id, children, className, ...rest }) => {
  const { layouts } = useContext(DynamicEngineContext);
  const saved = layouts[scope] || [];
  const row = saved.find((r) => r.section_id === id);
  const hidden = row ? !!row.hidden : false;
  if (hidden) return null;
  return (
    <div data-section-id={id} data-section-scope={scope} className={className} {...rest}>
      {children}
    </div>
  );
};
